import { toast } from 'react-toastify';

import { NUMBER_FORMATS } from '../constants/interfaces';
import { UNAUTHORIZED } from '../constants/statusCodes';
import formatNumber from './formatNumber';

import type { IPresignedData, IPresignedFields } from 'store/slices/files/interfaces/IPresignedFileResponse';

type TUnknownRecord = Record<string, unknown>;

const BAD_REQUEST_STATUS = 400;
const BACKEND_ERROR_CODE_PREFIX_REGEX = /^\d+(?:\.\d+)+:\s*/;

const isUnknownRecord = (value: unknown): value is TUnknownRecord => (
  typeof value === 'object' && value !== null
);

const getNumericStatus = (status: unknown): number | null => {
  if (typeof status === 'number') {
    return status;
  }

  if (typeof status !== 'string') {
    return null;
  }

  const numericStatus = Number(status);

  return Number.isNaN(numericStatus) ? null : numericStatus;
};

const getApiErrorStatus = (error: unknown): number | null => {
  if (!isUnknownRecord(error)) {
    return null;
  }

  return getNumericStatus(error.status) ?? getNumericStatus(error.originalStatus);
};

export const removeBackendErrorCodePrefix = (message: string): string => {
  const normalizedMessage = message.replace(BACKEND_ERROR_CODE_PREFIX_REGEX, '').trim();

  return normalizedMessage || message;
};

const extractFirstStringError = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    return trimmedValue || null;
  }

  if (Array.isArray(value)) {
    return value.reduce<string | null>((message, item) => {
      if (message) return message;
      return extractFirstStringError(item);
    }, null);
  }

  if (!isUnknownRecord(value)) return null;

  return extractFirstStringError(value.message)
    ?? extractFirstStringError(value.errors)
    ?? extractFirstStringError(value.error);
};

export const extractApiErrorMessage = (error: unknown): string | null => {
  if (isUnknownRecord(error) && 'data' in error) {
    const dataMessage = extractFirstStringError(error.data);
    if (dataMessage) return dataMessage;
  }

  return extractFirstStringError(error);
};

export const getNotificationErrorMessage = (error: unknown, fallbackMessage: string): string => (
  extractApiErrorMessage(error) || fallbackMessage
);

export const getBadRequestErrorMessage = (error: unknown): string | null => {
  if (getApiErrorStatus(error) !== BAD_REQUEST_STATUS) {
    return null;
  }

  const message = extractApiErrorMessage(error);

  return message ? removeBackendErrorCodePrefix(message) : null;
};

export const showBadRequestError = (error: unknown) => {
  const errorMessage = getBadRequestErrorMessage(error);

  if (!errorMessage) {
    return null;
  }

  return toast.error(errorMessage);
};

// The base query refreshes the token or signs the user out on 401, so a toast there would only
// duplicate that flow. Every other failure is surfaced with the message the backend sent.
export const showApiErrorToast = (error: unknown, fallbackMessage: string) => {
  if (getApiErrorStatus(error) === UNAUTHORIZED) {
    return null;
  }

  return toast.error(removeBackendErrorCodePrefix(getNotificationErrorMessage(error, fallbackMessage)));
};

export const getApiFieldErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (isUnknownRecord(error) && isUnknownRecord(error.data)) {
    const backendError = error.data.error;
    const backendMessage = error.data.message;

    if (typeof backendError === 'string' && backendError.trim()) {
      return backendError;
    }

    if (typeof backendMessage === 'string' && backendMessage.trim()) {
      return backendMessage;
    }
  }

  return getNotificationErrorMessage(error, fallbackMessage);
};

// Resolves to whether S3 accepted the file. It never rejects, so callers decide what a failed upload means.
export const uploadFileToS3 = (file: File, presignedData: IPresignedData): Promise<boolean> => {
  const { url, fields } = presignedData;

  const formData = new FormData();

  Object.keys(fields).forEach((key) => {
    formData.append(key, fields[key as keyof IPresignedFields]);
  });

  formData.append('Content-Type', file.type);
  formData.append('file', file);

  return fetch(url, {
    method: 'POST',
    body: formData,
  })
    .then((response) => response.ok)
    .catch(() => false);
};

export const getKeyByValue = <T extends Record<string, string>>(
  value: string | undefined,
  object: T,
): keyof T | undefined => (
    Object.keys(object).find((key) => object[key] === value)
  );

export const formatBigNumber = (value: string) => {
  if (!value) return '-';

  const match = value.match(/^([\d,]+)\s*([A-Z]+)\s*\(([\d.]+)%\)$/);
  if (!match) {
    const simpleMatch = value.match(/^([\d,]+)\s*([A-Z]+)$/);
    if (!simpleMatch) return value;

    const [, numStr, currency] = simpleMatch;
    const num = parseFloat(numStr.replace(/,/g, ''));
    if (num < 1_000_000) {
      return value;
    }
    const millions = num / 1_000_000;
    if (millions < 1000) {
      const withOneDecimal = millions.toFixed(1);
      const formatted = withOneDecimal.endsWith('.0')
        ? withOneDecimal.slice(0, -2)
        : withOneDecimal;
      const withCommas = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${withCommas}M ${currency}`;
    }
    const formattedMillions = Math.round(millions)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `${formattedMillions}M ${currency}`;
  }

  const [, numStr, currency, percentage] = match;
  const num = parseFloat(numStr.replace(/,/g, ''));
  if (num < 1_000_000) {
    return value;
  }
  const millions = num / 1_000_000;
  if (millions < 1000) {
    const withOneDecimal = millions.toFixed(1);
    const formatted = withOneDecimal.endsWith('.0')
      ? withOneDecimal.slice(0, -2)
      : withOneDecimal;
    const withCommas = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${withCommas}M ${currency} (${percentage}%)`;
  }
  const formattedMillions = Math.round(millions)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${formattedMillions}M ${currency} (${percentage}%)`;
};

export const formatRevenueAndPercentage = ({
  actual, percentage, currency,
}: {
  actual?: string | number,
  percentage?: string,
  currency?: string,
}): string => {
  let result;

  const percentageFormatted = percentage && formatNumber(
    {
      value: Number(percentage),
      type: NUMBER_FORMATS.percent,
    },
  );

  const actualFormatted = actual && formatNumber(
    {
      value: +actual,
      type: NUMBER_FORMATS.decimal,
    },
  );

  if (actual && percentage) {
    if (currency) {
      result = `${actualFormatted} ${currency} (${percentageFormatted})`;
    } else {
      result = `${actualFormatted} (${percentageFormatted})`;
    }
  } else if (actual) {
    if (currency) {
      result = `${actualFormatted} ${currency}`;
    } else {
      result = actual.toString();
    }
  } else if (percentage) {
    result = `${percentageFormatted}`;
  } else {
    result = '-';
  }

  return formatBigNumber(result);
};

export const realToSliderValueCoefficient = (value: string) => Math.round((+value - 0.1) / 0.1) - 9;

export const sliderToRealValueCoefficient = (sliderValue: number) => parseFloat(
  ((sliderValue + 9) * 0.1 + 0.1).toFixed(1),
);

export const shortBigNumber = ({
  actual,
}: {
  actual?: string | number,
}): string => {
  if (!actual) return '-';

  const num = Number(actual);

  if (num < 1_000_000) {
    const actualFormatted = formatNumber({
      value: num,
      type: NUMBER_FORMATS.decimal,
    });
    return String(actualFormatted) || String(actual);
  }

  const millions = num / 1_000_000;
  if (millions < 1000) {
    const withOneDecimal = millions.toFixed(1);
    const formatted = withOneDecimal.endsWith('.0')
      ? withOneDecimal.slice(0, -2)
      : withOneDecimal;
    const withCommas = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${withCommas}М`;
  }
  const formattedMillions = Math.round(millions)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${formattedMillions}М`;
};

export const extractErrorMessage = (response: { error?: unknown }): string | null => (
  extractApiErrorMessage(response.error)
);
